import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { companyInfo } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback reviews in case API fails
const fallbackReviews = [
  {
    id: 1,
    name: "Maria Popescu",
    rating: 5,
    date: "2026-03-08",
    text: "Serviciu excepțional! Canapeaua arată ca nouă. Echipa a fost foarte profesionistă și punctuală. Recomand cu încredere!",
    avatar: "MP"
  },
  {
    id: 2,
    name: "Ion Dragoș",
    rating: 5,
    date: "2026-03-01",
    text: "Am solicitat curățarea saltelei și sunt foarte mulțumit de rezultat. Mirosul neplăcut a dispărut complet. Mulțumesc!",
    avatar: "ID"
  },
  {
    id: 3,
    name: "Elena Munteanu",
    rating: 5,
    date: "2026-02-25",
    text: "Profesioniști adevărați! Au reușit să scoată pete foarte vechi de pe canapea. Prețul a fost corect și rezultatul extraordinar.",
    avatar: "EM"
  },
  {
    id: 4,
    name: "Andrei Constantin",
    rating: 5,
    date: "2026-02-18",
    text: "Recomand cu mare drag! Echipament profesional, oameni serioși. Canapeaua și saltelele arată impecabil.",
    avatar: "AC"
  }
];

const ReviewsSection = ({ t }) => {
  const [reviews, setReviews] = useState(fallbackReviews);
  const [averageRating, setAverageRating] = useState(5.0);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reviews`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setReviews(data);
            // Calculate average rating
            const avg = data.reduce((sum, r) => sum + r.rating, 0) / data.length;
            setAverageRating(Math.round(avg * 10) / 10);
          }
        }
      } catch (error) {
        console.log('Using fallback reviews');
      }
    };

    fetchReviews();
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <section id="reviews" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0d2e5d] mb-4">
            {t.reviews.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.reviews.subtitle}
          </p>
          
          {/* Google Rating Badge */}
          <div className="inline-flex items-center gap-3 mt-6 px-6 py-3 bg-white border-2 border-gray-200 rounded-full shadow-md hover:shadow-lg transition-shadow">
            <a
              href={companyInfo.googleBusiness}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3"
            >
              <img
                src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
                alt="Google"
                className="h-5"
              />
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-[#0d2e5d]">{averageRating}</span>
                <div className="flex">{renderStars(Math.round(averageRating))}</div>
              </div>
            </a>
          </div>
        </div>

        {/* Reviews Grid - Show only 3 */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reviews.slice(0, 3).map((review) => (
            <Card
              key={review.id}
              className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-[#2196f3]/30"
            >
              <CardContent className="p-6">
                {/* Quote Icon */}
                <div className="flex justify-between items-start mb-4">
                  <Quote className="w-8 h-8 text-[#35c6e1]/30" />
                  <div className="flex gap-0.5">{renderStars(review.rating)}</div>
                </div>

                {/* Review Text */}
                <p className="text-gray-700 leading-relaxed mb-6">
                  {review.text}
                </p>

                {/* Reviewer Info */}
                <div className="flex items-center gap-3 pt-4 border-t">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#2196f3] to-[#5cd26f] rounded-full flex items-center justify-center text-white font-bold">
                    {review.avatar}
                  </div>
                  <div>
                    <div className="font-bold text-[#0d2e5d]">{review.name}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(review.date).toLocaleDateString(t.nav.home === 'Acasă' ? 'ro-RO' : 'en-US', {
                        year: 'numeric',
                        month: 'long'
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* More Reviews Button */}
        <div className="text-center mt-10">
          <a
            href={companyInfo.googleBusiness}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white border-2 border-[#2196f3] text-[#2196f3] font-semibold rounded-full hover:bg-[#2196f3] hover:text-white transition-all duration-300 shadow-md hover:shadow-lg"
          >
            {t.nav.home === 'Acasă' ? 'Mai multe recenzii...' : 'More reviews...'}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>

        {/* Google Maps Link */}
        <div className="text-center mt-12">
          <a
            href={companyInfo.googleBusiness}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-6 py-3 bg-white border-2 border-[#2196f3] rounded-lg hover:bg-[#2196f3] hover:text-white transition-all duration-300 group"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C7.31 0 3.5 3.81 3.5 8.5c0 1.87.61 3.61 1.66 5.03l.01.01L12 24l6.83-10.46.01-.01c1.05-1.42 1.66-3.16 1.66-5.03C20.5 3.81 16.69 0 12 0zm0 11.5c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
            </svg>
            <div className="text-left">
              <div className="font-bold">
                {t.nav.home === 'Acasă' ? 'Ne găsești și pe Google Maps' : 'Find us on Google Maps'}
              </div>
              <div className="text-sm opacity-75">
                {t.nav.home === 'Acasă' ? 'Recenzii și locație' : 'Reviews and location'}
              </div>
            </div>
            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
